// client/src/context/StoreContext.js
// Public branding for whichever store this domain resolves to (name, logo,
// theme colors) - fetched unauthenticated via GET /api/store/current, which
// the server resolves from the request's Host header. No auth dependency,
// so this can sit above AuthProvider/ThemeModeProvider in the tree (theme
// color derivation needs it). The super-admin's store *switcher* is a
// separate concern - see ActingStoreContext, which does need to know who's
// logged in and so lives lower in the tree, inside App.js.
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../utils/media';

const DEFAULT_STORE = {
  id: null,
  slug: null,
  name: 'Store',
  primary_color: '#3563E9',
  secondary_color: '#DC3545',
  success_color: '#22A45D',
  warning_color: '#ED6C02',
  error_color: '#D32F2F',
  logo_url: null,
  banner_url: null,
  // NULL is a real, intentional value for these two (unlike the 5 colors
  // above) - it means "no override, follow light/dark mode" and must stay
  // NULL rather than getting a hex default, or every store would be
  // permanently pinned to one background regardless of dark mode.
  background_color: null,
  text_color: null,
  support_email: null,
};

export const StoreContext = createContext({ store: DEFAULT_STORE, loaded: false });

export const StoreProvider = ({ children }) => {
  const [store, setStore] = useState(DEFAULT_STORE);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/store/current`);
        setStore(response.data);
        if (response.data.name) document.title = response.data.name;
        // Swap the browser tab icon to this store's logo, falling back to
        // the default favicon.ico for stores that haven't uploaded one.
        const faviconLink = document.querySelector("link[rel~='icon']");
        if (faviconLink) {
          faviconLink.href = response.data.logo_url
            ? getImageUrl(response.data.logo_url)
            : `${process.env.PUBLIC_URL}/favicon.ico`;
        }
      } catch (error) {
        console.error('Failed to fetch store info', error);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  return (
    <StoreContext.Provider value={{ store, loaded }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);
